import store from '@/store'
import _ from 'lodash'
import { CLEAR, INIT, SET_LOADING_STATUS, SET_STATUS_MESSAGE, SPLICE, SET_TOTAL_COUNT } from './mutation-types'
import {
  UPDATE_CATEGORY_RULE,
  UPDATE_GROUP_RULE,
  UPDATE_INTEREST_RULE,
  UPDATE_PROFILE_RULE,
  UPDATE_RULE_TYPE_BY_INDEX,
  UPDATE_RULE_NAME_BY_INDEX,
  UPDATE_REPEATED_BY_INDEX,
  UPDATE_APPLY_TO_NEW_BY_INDEX,
  UPDATE_FREQUENCY_BY_INDEX,
  UPDATE_SKIP_CONTACTED_WITHIN_BY_INDEX,
  UPDATE_REMIND_DAYS_BY_INDEX,
  UPDATE_APPLY_TO_BIRTHDAY_BY_INDEX,
  UPDATE_APPLY_TO_ALL_BY_INDEX,
  UPDATE_SCHEDULED_DATE_TIME_BY_INDEX,
  UPDATE_RULE_MESSAGE_BY_INDEX,
  UPDATE_TIME_ZONE_BY_INDEX,
  ADD_RULE_MESSAGE,
  DELETE_RULE_MESSAGE
} from './automation-mutation-types'
import { DELETE_RULE, DELETE_MESSAGE } from '@/backend/graphql/automationGQL'
import { apolloClient } from '@/plugins/apollo/apolloUploadClient'
import { chatAxios, CREATE_PROFILE_AUTO_MESSAGE, CREATE_GROUP_AUTO_MESSAGE,
  CREATE_CATEGORY_AUTO_MESSAGE, CREATE_INTEREST_AUTO_MESSAGE,
  UPDATE_PROFILE_AUTO_MESSAGE, UPDATE_GROUP_AUTO_MESSAGE, UPDATE_CATEGORY_AUTO_MESSAGE,
  UPDATE_INTEREST_AUTO_MESSAGE, DELETE_AUTO_MESSAGE
} from '@/backend/vue-axios'

function isValidRule (state, index) {
  return (index >= 0 && index < state.rules.length) ? (!!state.rules[index]) : false
}

function getAcceptedKeys (objectName) {
  let acceptedKeys = []
  switch (objectName) {
    case 'rule':
      acceptedKeys = ['id', 'name', 'ruleType', 'repeated', 'applyToNew',
        'frequency', 'applyToBirthday', 'applyToAll', 'remindDays', 'skipContactedWithin', 'timeZone',
        'scheduledDateTime', 'profile', 'ruleMessage']
      break
    case 'ruleMessage':
      acceptedKeys = ['id', 'body']
      break
    case 'profile':
      acceptedKeys = ['id']
      break
    default:
      break
  }
  return acceptedKeys
}

function cleanUpDataByKey (data, key, acceptedKeys) {
  if (!acceptedKeys.includes(key)) {
    delete data[key]
  }
}

function cleanUpData (data, acceptedKeys) {
  if (_.isEmpty(acceptedKeys)) return
  for (const key of Object.keys(data)) {
    cleanUpDataByKey(data, key, acceptedKeys)
    cleanUpData(data[key], getAcceptedKeys(key))
  }
}

async function createAutoMessage ({ ruleData, ruleId, ruleHolderId }) {
  const accountSid = _.get(store.getters['account/getUser'], 'twilio_sid', '')
  const authToken = store.getters['authentication/getChatAuthToken']
  const chatBearAuthToken = store.getters['authentication/getChatBearerToken']
  chatAxios.defaults.headers.common['Authorization'] = `bearer ${chatBearAuthToken}`

  let url = ''
  if (ruleData.profileId === 0 || ruleData.profileId) {
    url = CREATE_PROFILE_AUTO_MESSAGE
  } else if (ruleData.groupId === 0 || ruleData.groupId) {
    url = CREATE_GROUP_AUTO_MESSAGE
  } else if (ruleData.categoryId === 0 || ruleData.categoryId) {
    url = CREATE_CATEGORY_AUTO_MESSAGE
  } else if (ruleData.interestId === 0 || ruleData.interestId) {
    url = CREATE_INTEREST_AUTO_MESSAGE
  }
  try {
    const res = await chatAxios.post(url, { accountSid, authToken, ruleId, ruleHolderId })
    return [null, res]
  } catch (error) {
    return [error, null]
  }
}

async function updateAutoMessage ({ ruleData, ruleId, ruleHolderId }) {
  const accountSid = _.get(store.getters['account/getUser'], 'twilio_sid', '')
  const authToken = store.getters['authentication/getChatAuthToken']
  const chatBearAuthToken = store.getters['authentication/getChatBearerToken']
  chatAxios.defaults.headers.common['Authorization'] = `bearer ${chatBearAuthToken}`

  let url = ''
  if (ruleData.profile) {
    url = UPDATE_PROFILE_AUTO_MESSAGE
  } else if (ruleData.group) {
    url = UPDATE_GROUP_AUTO_MESSAGE
  } else if (ruleData.category) {
    url = UPDATE_CATEGORY_AUTO_MESSAGE
  } else if (ruleData.interest) {
    url = UPDATE_INTEREST_AUTO_MESSAGE
  }
  try {
    const res = await chatAxios.put(url, { accountSid, authToken, ruleId, ruleHolderId })
    return [null, res]
  } catch (error) {
    return [error, null]
  }
}

async function deleteAutoMessage ({ ruleId, profileIds }) {
  const chatBearAuthToken = store.getters['authentication/getChatBearerToken']
  try {
    const data = { ruleId, profileIds }
    chatAxios.defaults.headers.common['Authorization'] = `bearer ${chatBearAuthToken}`
    const res = await chatAxios.delete(DELETE_AUTO_MESSAGE, { data })
    return [null, res]
  } catch (error) {
    return [error, null]
  }
}

const state = {
  rules: [],
  totalCount: 0,
  status: {
    loading: false,
    message: 'Loading Rules. Please wait ...'
  }
}

const getters = {
  getLoadingStatus: (state) => {
    return state.status.loading
  },
  getLoadingText: (state) => {
    return state.status.message
  },
  getEnabledByIndex: (state) => (index) => {
    if (!isValidRule(state, index)) return null
    return state.rules[index].profile.length > 0
  },
  getAllRules: (state) => {
    return state.rules
  },
  getTotalCount: (state) => {
    return state.totalCount
  }
}

const actions = {
  setLoadingStatus ({ commit }, { val }) {
    commit(SET_LOADING_STATUS, { val })
  },
  setStatusMessage ({ commit }, { message }) {
    commit(SET_STATUS_MESSAGE, { message })
  },
  async init ({ commit }, { newRules, totalCount }) {
    commit(SET_LOADING_STATUS, { val: false })
    try {
      const newRuleObjects = JSON.parse(JSON.stringify(newRules))
      commit(INIT, { newRuleObjects, totalCount: newRuleObjects.length })
    } catch (error) {
      store.dispatch('alert/error', error.messages)
    }
  },
  async updateRuleNameByIndex ({ commit }, { index, value }) {
    commit(UPDATE_RULE_NAME_BY_INDEX, { index, value })
  },
  async updateRuleTypeByIndex ({ commit }, { index, value }) {
    commit(UPDATE_RULE_TYPE_BY_INDEX, { index, value })
  },
  async updateRepeatedByIndex ({ commit }, { index, value }) {
    commit(UPDATE_REPEATED_BY_INDEX, { index, value })
  },
  async updateApplyToNewByIndex ({ commit }, { index, value }) {
    commit(UPDATE_APPLY_TO_NEW_BY_INDEX, { index, value })
  },
  async updateFrequencyByIndex ({ commit }, { index, value }) {
    commit(UPDATE_FREQUENCY_BY_INDEX, { index, value })
  },
  async updateSkipContactedWithinByIndex ({ commit }, { index, value }) {
    commit(UPDATE_SKIP_CONTACTED_WITHIN_BY_INDEX, { index, value })
  },
  async updateRemindDaysByIndex ({ commit }, { index, value }) {
    commit(UPDATE_REMIND_DAYS_BY_INDEX, { index, value })
  },
  async updateApplyToBirthdayByIndex ({ commit }, { index, value }) {
    commit(UPDATE_APPLY_TO_BIRTHDAY_BY_INDEX, { index, value })
  },
  async updateApplyToAllByIndex ({ commit }, { index, value }) {
    commit(UPDATE_APPLY_TO_ALL_BY_INDEX, { index, value })
  },
  async updateScheduledDateTimeByIndex ({ commit }, { index, value }) {
    commit(UPDATE_SCHEDULED_DATE_TIME_BY_INDEX, { index, value })
  },
  async updateTimeZoneByIndex ({ commit }, { index, value }) {
    commit(UPDATE_TIME_ZONE_BY_INDEX, { index, value })
  },
  async updateRuleMessageByIndex ({ commit }, { ruleIndex, messageIndex, value }) {
    commit(UPDATE_RULE_MESSAGE_BY_INDEX, { ruleIndex, messageIndex, value })
  },
  async updateProfileRuleByIndex ({ commit }, { index, profileId }) {
    commit(UPDATE_PROFILE_RULE, { index, profileId })
  },
  async updateGroupRuleByIndex ({ commit }, { index, groupId }) {
    commit(UPDATE_GROUP_RULE, { index, groupId })
  },
  async updateCategoryRuleByIndex ({ commit }, { index, categoryId }) {
    commit(UPDATE_CATEGORY_RULE, { index, categoryId })
  },
  async updateInterestRuleByIndex ({ commit }, { index, interestId }) {
    commit(UPDATE_INTEREST_RULE, { index, interestId })
  },
  async next ({ commit }, { newRules, startIndex }) {
    commit(SET_LOADING_STATUS, { val: false })
    try {
      const newRuleObjects = JSON.parse(JSON.stringify(newRules))
      commit(SPLICE, { newRuleObjects, startIndex, deleteCount: newRules.length })
    } catch (error) {
      store.dispatch('alert/error', error.message)
    }
  },
  async unshift ({ commit }, { newRules }) {
    const newRuleObjects = JSON.parse(JSON.stringify(newRules))
    commit(SPLICE, { newRuleObjects, startIndex: 0, deleteCount: 0 })
    commit(SET_TOTAL_COUNT, { val: state.totalCount + newRuleObjects.length })
    if (state.status.loading) { commit(SET_LOADING_STATUS, { val: false }) }
  },
  async createRule ({ commit }, { ruleData, gql, ruleHolderId }) {
    commit(SET_STATUS_MESSAGE, { message: 'Creating a rule...' })
    commit(SET_LOADING_STATUS, { val: true })
    try {
      const res = await apolloClient.mutate({
        mutation: gql,
        variables: { ruleData: ruleData }
      })
      if (!res.errors) {
        const success = JSON.parse(JSON.stringify(res.data.createRule.success))
        const message = JSON.parse(JSON.stringify(res.data.createRule.message))
        if (!success) {
          store.dispatch('alert/error', message)
          commit(SET_LOADING_STATUS, { val: false })
          return false
        }
        const [createMessageErr] =
          await createAutoMessage({ ruleData, ruleId: res.data.createRule.rule.id, ruleHolderId })
        if (createMessageErr) {
          store.dispatch('alert/error', createMessageErr.errors.message)
          commit(SET_LOADING_STATUS, { val: false })
          return false
        }
        store.dispatch('automation/unshift', { newRules: [res.data.createRule.rule] })
      } else {
        store.dispatch('alert/error', res.errors.message)
        commit(SET_LOADING_STATUS, { val: false })
        return false
      }
    } catch (error) {
      commit(SET_LOADING_STATUS, { val: false })
      store.dispatch('alert/error', error.message)
      return false
    }
    return true
  },
  async updateRule ({ commit }, { ruleData, index, gql, ruleHolderId }) {
    if (!ruleData || !ruleData.id) return
    let copyRuleData = JSON.parse(JSON.stringify(ruleData))
    commit(SET_STATUS_MESSAGE, { message: 'Updating a rule...' })
    commit(SET_LOADING_STATUS, { val: true })
    try {
      const acceptedContactKeys = getAcceptedKeys('rule')
      for (const key of Object.keys(copyRuleData)) {
        if (key === 'profile' && copyRuleData.hasOwnProperty(key)) {
          copyRuleData['profileEnabled'] = copyRuleData[key]
            .filter((item) => Number(item.id) === Number(ruleHolderId)).length > 0
          copyRuleData['profileId'] = ruleHolderId
          delete copyRuleData[key]
        } else if (key === 'group' && copyRuleData.hasOwnProperty(key)) {
          copyRuleData['groupEnabled'] = copyRuleData[key]
            .filter((item) => Number(item.id) === Number(ruleHolderId)).length > 0
          copyRuleData['groupId'] = ruleHolderId
          delete copyRuleData[key]
        } else if (key === 'category' && copyRuleData.hasOwnProperty(key)) {
          copyRuleData['categoryEnabled'] = copyRuleData[key]
            .filter((item) => Number(item.id) === Number(ruleHolderId)).length > 0
          copyRuleData['categoryId'] = ruleHolderId
          delete copyRuleData[key]
        } else if (key === 'interest' && copyRuleData.hasOwnProperty(key)) {
          copyRuleData['interestEnabled'] = copyRuleData[key]
            .filter((item) => Number(item.id) === Number(ruleHolderId)).length > 0
          copyRuleData['interestId'] = ruleHolderId
          delete copyRuleData[key]
        } else if (key === 'ruleMessage' && copyRuleData.hasOwnProperty(key)) {
          const ruleMessageSet = []
          for (const ruleMessage of copyRuleData[key]) {
            const msgBody = ruleMessage.body
            if (msgBody && /\S/.test(msgBody)) {
              cleanUpData(ruleMessage, getAcceptedKeys(key))
              ruleMessageSet.push(ruleMessage)
            }
          }
          copyRuleData[key] = ruleMessageSet
        } else {
          cleanUpDataByKey(copyRuleData, key, acceptedContactKeys)
        }
      }
      const res = await apolloClient.mutate({
        mutation: gql,
        variables: { ruleData: copyRuleData }
      })
      if (!res.errors) {
        const success = JSON.parse(JSON.stringify(res.data.updateRule.success))
        const resMessage = JSON.parse(JSON.stringify(res.data.updateRule.message))
        if (!success) {
          store.dispatch('alert/error', resMessage)
          commit(SET_LOADING_STATUS, { val: false })
          return false
        }

        const [updateMessageErr] =
          await updateAutoMessage({ ruleData, ruleId: res.data.updateRule.rule.id, ruleHolderId })
        if (updateMessageErr) {
          store.dispatch('alert/error', updateMessageErr.errors.message)
          commit(SET_LOADING_STATUS, { val: false })
          return false
        }

        const message = 'Update Contact Successfully'
        store.dispatch('alert/success', message)
        const newRules = [res.data.updateRule.rule]
        const newRuleObjects = JSON.parse(JSON.stringify(newRules))
        commit(SPLICE, { newRuleObjects, startIndex: index, deleteCount: newRuleObjects.length })
      } else {
        store.dispatch('alert/error', res.errors.message)
      }
      commit(SET_LOADING_STATUS, { val: false })
    } catch (error) {
      commit(SET_LOADING_STATUS, { val: false })
      store.dispatch('alert/error', error.message)
      return false
    }
    return true
  },
  async deleteRule ({ commit }, { ruleData }) {
    commit(SET_STATUS_MESSAGE, { message: 'Deleting a rule...' })
    commit(SET_LOADING_STATUS, { val: true })
    try {
      const res = await apolloClient.mutate({
        mutation: DELETE_RULE,
        variables: { ruleData: ruleData }
      })
      if (!res.errors) {
        const success = JSON.parse(JSON.stringify(res.data.deleteRule.success))
        const message = JSON.parse(JSON.stringify(res.data.deleteRule.message))
        const profileIds = JSON.parse(JSON.stringify(res.data.deleteRule.profileIds))
        if (!success) {
          store.dispatch('alert/error', message)
          commit(SET_LOADING_STATUS, { val: false })
          return false
        }
        const [deleteMessageErr] =
          await deleteAutoMessage({ ruleId: ruleData.id, profileIds })
        if (deleteMessageErr) {
          store.dispatch('alert/error', deleteMessageErr.errors.message)
          commit(SET_LOADING_STATUS, { val: false })
          return false
        }
        const newRuleObjects = state.rules.filter(rule => rule.id !== ruleData.id)
        commit(INIT, { newRuleObjects, totalCount: newRuleObjects.length })
        commit(SET_STATUS_MESSAGE, { message: 'Remove rule successfully' })
      } else {
        store.dispatch('alert/error', res.errors.message)
      }
      commit(SET_LOADING_STATUS, { val: false })
    } catch (error) {
      commit(SET_LOADING_STATUS, { val: false })
      store.dispatch('alert/error', error.message)
      return false
    }
    return true
  },
  addRuleMessage ({ commit }, { ruleIndex, newMessageData }) {
    commit(ADD_RULE_MESSAGE, { ruleIndex, newMessageData })
  },
  async deleteRuleMessage ({ commit }, { ruleIndex, messageData }) {
    commit(SET_STATUS_MESSAGE, { message: 'Deleting a message...' })
    commit(SET_LOADING_STATUS, { val: true })
    try {
      const res = await apolloClient.mutate({
        mutation: DELETE_MESSAGE,
        variables: { messageData }
      })
      if (!res.errors) {
        const success = JSON.parse(JSON.stringify(res.data.deleteRuleMessage.success))
        const message = JSON.parse(JSON.stringify(res.data.deleteRuleMessage.message))
        if (!success) {
          store.dispatch('alert/error', message)
        } else {
          commit(DELETE_RULE_MESSAGE, { ruleIndex, messageId: messageData.id })
        }
      } else {
        store.dispatch('alert/error', res.errors.message)
      }
      commit(SET_LOADING_STATUS, { val: false })
    } catch (error) {
      commit(SET_LOADING_STATUS, { val: false })
      store.dispatch('alert/error', error.message)
      return false
    }
    return true
  },
  async clear ({ commit }) {
    commit(CLEAR)
  }
}

const mutations = {
  [SET_LOADING_STATUS] (state, { val }) {
    state.status.loading = val
  },
  [SET_STATUS_MESSAGE] (state, { message }) {
    state.status.message = message
  },
  [INIT] (state, { newRuleObjects, totalCount }) {
    state.totalCount = totalCount
    state.rules = newRuleObjects
  },
  [SPLICE] (state, { newRuleObjects, startIndex, deleteCount }) {
    // TODO: clean up rule data if needed
    state.rules.splice(startIndex, deleteCount, ...newRuleObjects)
  },
  [UPDATE_PROFILE_RULE] (state, { index, profileId }) {
    if (!isValidRule(state, index)) return
    const profile = _.get(state.rules[index], 'profile', [])
    const hasProfile = profile.some((p, i) => {
      if (Number(p.id) === Number(profileId)) {
        profile.splice(i, 1)
        return true
      }
      return false
    })
    if (!hasProfile) { profile.push({ id: profileId }) }
  },
  [UPDATE_GROUP_RULE] (state, { index, groupId }) {
    if (!isValidRule(state, index)) return
    const group = _.get(state.rules[index], 'group', [])
    const hasGroup = group.some((p, i) => {
      if (Number(p.id) === Number(groupId)) {
        group.splice(i, 1)
        return true
      }
      return false
    })
    if (!hasGroup) { group.push({ id: groupId }) }
  },
  [UPDATE_CATEGORY_RULE] (state, { index, categoryId }) {
    if (!isValidRule(state, index)) return
    const category = _.get(state.rules[index], 'category', [])
    const hasCategory = category.some((p, i) => {
      if (Number(p.id) === Number(categoryId)) {
        category.splice(i, 1)
        return true
      }
      return false
    })
    if (!hasCategory) { category.push({ id: categoryId }) }
  },
  [UPDATE_INTEREST_RULE] (state, { index, interestId }) {
    if (!isValidRule(state, index)) return
    const interest = _.get(state.rules[index], 'interest', [])
    const hasInterest = interest.some((p, i) => {
      if (Number(p.id) === Number(interestId)) {
        interest.splice(i, 1)
        return true
      }
      return false
    })
    if (!hasInterest) { interest.push({ id: interestId }) }
  },
  [UPDATE_RULE_TYPE_BY_INDEX] (state, { index, value }) {
    if (!isValidRule(state, index)) return
    state.rules[index].ruleType = value
  },
  [UPDATE_RULE_NAME_BY_INDEX] (state, { index, value }) {
    if (!isValidRule(state, index)) return
    state.rules[index].name = value
  },
  [UPDATE_REPEATED_BY_INDEX] (state, { index, value }) {
    if (!isValidRule(state, index)) return
    state.rules[index].repeated = value || false
  },
  [UPDATE_APPLY_TO_NEW_BY_INDEX] (state, { index, value }) {
    if (!isValidRule(state, index)) return
    state.rules[index].applyToNew = value || false
  },
  [UPDATE_FREQUENCY_BY_INDEX] (state, { index, value }) {
    if (!isValidRule(state, index)) return
    if (value === 0 || value) {
      state.rules[index].frequency = value
    }
  },
  [UPDATE_SKIP_CONTACTED_WITHIN_BY_INDEX] (state, { index, value }) {
    if (!isValidRule(state, index)) return
    if (value === 0 || value) {
      state.rules[index].skipContactedWithin = value
    }
  },
  [UPDATE_REMIND_DAYS_BY_INDEX] (state, { index, value }) {
    if (!isValidRule(state, index)) return
    if (value === 0 || value) {
      state.rules[index].remindDays = value
    }
  },
  [UPDATE_APPLY_TO_BIRTHDAY_BY_INDEX] (state, { index, value }) {
    if (!isValidRule(state, index)) return
    state.rules[index].applyToBirthday = value || false
  },
  [UPDATE_APPLY_TO_ALL_BY_INDEX] (state, { index, value }) {
    if (!isValidRule(state, index)) return
    state.rules[index].applyToAll = value || false
  },
  [UPDATE_SCHEDULED_DATE_TIME_BY_INDEX] (state, { index, value }) {
    if (!isValidRule(state, index)) return
    state.rules[index].scheduledDateTime = value
  },
  [UPDATE_TIME_ZONE_BY_INDEX] (state, { index, value }) {
    if (!isValidRule(state, index)) return
    state.rules[index].timeZone = value
  },
  [UPDATE_RULE_MESSAGE_BY_INDEX] (state, { ruleIndex, messageIndex, value }) {
    if (!isValidRule(state, ruleIndex)) return
    state.rules[ruleIndex].ruleMessage[messageIndex].body = value
  },
  [DELETE_RULE_MESSAGE] (state, { ruleIndex, messageId }) {
    if (!isValidRule(state, ruleIndex)) return
    state.rules[ruleIndex].ruleMessage =
      state.rules[ruleIndex].ruleMessage.filter((message) => message.id !== messageId)
  },
  [ADD_RULE_MESSAGE] (state, { ruleIndex, newMessageData }) {
    if (!isValidRule(state, ruleIndex)) return
    state.rules[ruleIndex].ruleMessage.unshift(newMessageData)
  },
  [SET_TOTAL_COUNT] (state, { val }) {
    state.totalCount = val
  },
  [CLEAR] (state) {
    state.rules = []
    state.status = {
      loading: false,
      message: 'Loading Rules. Please wait ...'
    }
    state.totalCount = 0
  }
}

export const automation = {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
}
